import { QueryBuilder } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import LanguageIcon from "@mui/icons-material/Language";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import {
  Avatar,
  Box,
  Divider,
  Grid,
  Stack,
  Typography,
  // useTheme,
} from '@mui/material';
import { stringAvatar } from "utils/functions";
import { StyledAvatarCustom } from 'component/commonComponents/Avatar/Avatar.style';
import React from 'react';
import HlxButton from '../../commonComponents/button/button';
import { OverlayContainer, StyledButton, StyledCircularProgress } from './overlay.style';
import { useTheme as useUptimeTheme } from "styled-components";
import { useCustomLayout, useHyperlinkStyle } from "../CustomLayout/CustomLayoutContext";

export interface IStyledOverlayProps {
  loading?: boolean;
  userInfo?: any;
  locale?: Record<string, string>;
  verifiedDate?: string;
  language?: any;
  enabledLanguage?: boolean;
  isEnableActOnBehalf?: boolean;
  openOnBehalfModal?: () => void;
  handleLogout?: () => void;
  handleEdit?: () => {};
  imgAvatar?:any;
  isOnBehalf?: boolean;
  isPcOrderOBO?: boolean;
}

interface ActOnBehalfIconProps {
  color: string;
  className?: string;
}

export const ActOnBehalfIcon: React.FC<ActOnBehalfIconProps> = ({ color, className }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
    >
      <path
        d="M15.0932 5.49701H15.7966V7.62575L20 3.81737L15.7966 0V2.53293H15.0932C11.3983 2.53293 9.31356 5.43413 7.48305 7.99401C5.83051 10.2934 4.40678 12.2874 2.09322 12.2874H0V15.2515H2.09322C5.78814 15.2515 7.87288 12.3503 9.70339 9.79042C11.3559 7.48204 12.7881 5.49701 15.0932 5.49701ZM5.40678 7.36527C5.5678 7.1497 5.72034 6.92515 5.88136 6.70958C6.27119 6.16168 6.68644 5.58683 7.13559 5.02096C5.82203 3.7006 4.22034 2.73952 2.09322 2.73952H0V5.70359H2.09322C3.42373 5.70359 4.45763 6.36826 5.40678 7.36527ZM15.7966 12.494H15.0932C13.6864 12.494 12.6017 11.7485 11.6017 10.6527C11.5 10.7874 11.3983 10.9311 11.2966 11.0749C10.8559 11.6856 10.3814 12.3503 9.85593 12.997C11.2119 14.3982 12.8559 15.4581 15.0847 15.4581H15.7881V18L19.9915 14.1826L15.7881 10.3653V12.494H15.7966Z"
        fill={color}
      />
    </svg>
  );
};

const ArrowUpIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="21"
      height="14"
      viewBox="0 0 21 14"
      fill="none"
    >
      <path
        d="M20.1329 8.78715L12.5582 0.891669C11.4176 -0.297225 9.57515 -0.297225 8.43456 0.891669L0.859867 8.78715C-0.982626 10.7077 0.333441 14 2.93633 14L18.0857 14C20.6886 14 21.9754 10.7077 20.1329 8.78715Z"
        fill="#F5F5FF"
      />
    </svg>
  );
};

const gridItemProps = {
  label: {
    xs: 5,
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  value: {
    xs: 7,
  },
};

const Overlay: React.FC<IStyledOverlayProps> = ({
  loading,
  userInfo,
  locale,
  verifiedDate,
  imgAvatar,
  language,
  enabledLanguage,
  isEnableActOnBehalf = false,
  openOnBehalfModal,
  handleEdit,
  handleLogout,
  isOnBehalf,
  isPcOrderOBO,
}) => {
  const uptimeTheme = useUptimeTheme();
  // const theme = useTheme();

  const userName = userInfo?.name || userInfo?.full_name || `${userInfo?.first_name} ${userInfo?.last_name}`;

  const customLayoutConfig = useCustomLayout();
  const { isUseHyperlinkStyle } = customLayoutConfig;

  const { hyperlinkStyle } = useHyperlinkStyle();

  return (
    <OverlayContainer>
      <Box position="absolute" right="16px" top="-12px">
        <ArrowUpIcon />
      </Box>
      {loading ? (
        <Box margin="16px auto">
          <StyledCircularProgress />
        </Box>
      ) : (
        <Grid container display="flex" alignItems="center" padding="24px 20px">
          <Box display="flex" marginBottom="16px" gap="16px">
            {!!imgAvatar ? (
              <Avatar
                sx={{ width: 40, height: 40 }}
                src={imgAvatar}
                alt="user avatar"
                slotProps={{
                  img: {
                    title: userInfo?.name,
                  }
                }}
              />
            ) : (
              <StyledAvatarCustom>
                <Avatar {...stringAvatar(userInfo?.avatar_name || userName, 40)} />
              </StyledAvatarCustom>
            )}
            <Typography alignSelf="center" fontSize="16px" color="text.primary">
              {userName}
            </Typography>
          </Box>
          <Stack width="100%" direction="row" gap="20px" paddingY="8px">
            {!isOnBehalf && !isPcOrderOBO && !userInfo?.lastWorkingDate && !userInfo?.new_joiner && (
                <StyledButton variant="text" onClick={handleEdit} disableTouchRipple={isUseHyperlinkStyle} sx={{ ...hyperlinkStyle }} id="btn-overlay-editprofile">
                  <Stack direction="row" alignItems="center" gap="12px">
                    <EditIcon 
                      fontSize="small"
                      className="edit-icon"
                      sx={{ color: uptimeTheme.uptimeColors?.generalColors?.button?.hyperlink?.enabled }} 
                    />
                    <Typography
                      fontSize="14px"
                      fontWeight={600}
                      className="edit-btn"
                    >
                      {locale?.editProfile}
                    </Typography>
                  </Stack>
                </StyledButton>
              )
            }
            {!!isEnableActOnBehalf && !isOnBehalf && !isPcOrderOBO && !userInfo?.lastWorkingDate && !userInfo?.new_joiner && (
              <StyledButton variant="text" onClick={openOnBehalfModal} id="btn-overlay-aob">
                <Stack direction="row" alignItems="center" gap="12px">
                  <ActOnBehalfIcon className="abo-icon" color={uptimeTheme.uptimeColors?.generalColors?.button?.hyperlink?.enabled}/>
                  <Typography
                    fontSize="14px"
                    fontWeight={600}
                    className="abo-btn-link"
                    sx={{ color: uptimeTheme.uptimeColors?.generalColors?.button?.hyperlink?.enabled }}
                  >
                    {locale?.actOnBehalfOf}
                  </Typography>
                </Stack>
              </StyledButton>
            )}
          </Stack>
          <Grid
            container
            direction="row"
            display="flex"
            alignItems="center"
            gap="25px"
          >
            <Box width="100%" sx={{ bgcolor: "action.divider" }}>
              <Divider />
            </Box>
            <Grid item xs={12} display="flex">
              <Grid item {...gridItemProps.label}>
                <LocationOnIcon fontSize="small" color="action" />
                <Typography color="#000000CC" variant="body2">
                  {locale?.location}
                </Typography>
              </Grid>
              <Grid item {...gridItemProps.value}>
                <Typography color="text.secondary" variant="body2">
                  {userInfo.location}
                </Typography>
              </Grid>
            </Grid>
            {enabledLanguage && (
              <Grid item xs={12} display="flex" alignItems="center">
                <Grid item {...gridItemProps.label}>
                  <LanguageIcon fontSize="small" color="action" />
                  <Typography color="#000000CC" variant="body2">
                    {locale?.language}
                  </Typography>
                </Grid>
                <Grid item {...gridItemProps.value}>
                  <Typography color="text.secondary" variant="body2">
                    {language || "English"}
                  </Typography>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12} display="flex">
              <Grid item {...gridItemProps.label}>
                <PersonIcon fontSize="small" color="action" />
                <Typography color="#000000CC" variant="body2">
                  {locale?.reportsTo}
                </Typography>
              </Grid>
              <Grid item {...gridItemProps.value}>
                <Typography color="text.secondary" variant="body2">
                  {userInfo?.manager_name}
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={12} display="flex">
              <Grid item {...gridItemProps.label}>
                <QueryBuilder fontSize="small" color="action" />
                <Typography color="#000000CC" variant="body2">
                  {locale?.lastVerified}
                </Typography>
              </Grid>
              <Grid item {...gridItemProps.value}>
                <Typography color="text.secondary" variant="body2">
                  {verifiedDate}
                </Typography>
              </Grid>
            </Grid>
            <Box width="100%" sx={{ bgcolor: "action.divider" }}>
              <Divider />
            </Box>
            <Grid item xs={12} display="flex" justifyContent="center">
              <Box width="60%">
                <HlxButton
                  fullWidth={true}
                  variant="contained"
                  label={locale?.signOut}
                  onClick={handleLogout}
                  disabled={isOnBehalf}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      )}
    </OverlayContainer>
  );
};

Overlay.defaultProps = {
  loading: false,
  userInfo: {
    avatar_name: "Bang Nguyen",
    city: "Ho Chi Minh City",
    country: "VNM",
    email: "bnguyen42@dxc.com",
    employee_number: "1122493",
    first_name: "Cong Bang",
    last_verified: "2020-04-01T00:00:00.000Z",
    last_name: "Nguyen",
    location: "5 Building",
    locationId: "c38d1cbedb16b85018e16462ca9619c6",
    manager_name: "Dac Lam Ngo",
    mobile_phone: "",
    name: "Cong Bang Nguyen",
    state: "SG",
    uid: "f41cf45edbe23010bf85ce1c299619eb",
    user_name: "bnguyen42@dxc.com",
    lockedOut: false,
  },
  verifiedDate: '2 days ago',
  handleLogout: () => { },
  locale: {
    location: "Location",
    language: "Language",
    reportsTo: "Reports to",
    signOut: "Sign Out",
    lastVerified: "Last verified",
    editProfile: "Edit Profile",
  },
};

export default Overlay;
